import { R } from '@core/utils/r';
import { TableActions } from '@modules/common/TableActions';
import React, { ComponentType, CSSProperties, Fragment, MemoExoticComponent, ReactNode, useMemo, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { useCanHover } from '@core/utils/hooks/dom';

export interface BaseWidgetProps<C extends MemoExoticComponent<any>> {
  Component: C;
  id?: string;
  props: any;
  onEdit?(): void;
  onDelete?(): void;
  onClick?(): void;
  onView?(): void;
  onAdd?(): void;
  onCopy?(): void;
  children?: ReactNode;
  isEditing?: boolean;
  style?: CSSProperties;
  hideView?: boolean;
  print?: boolean;
  loading?: boolean;
}

export function BaseWidget<C extends MemoExoticComponent<any>>(baseProps: BaseWidgetProps<C>) {
  const { Component, props, isEditing, hideView = false, id = R.random(100, 9999), loading = false } = baseProps;
  const [isHover, setHover] = useState(false);
  const canHover = useCanHover();

  const widgetId = useMemo(() => R.random(), []);
  const isLibrary = useMemo(() => !!baseProps.onClick, [baseProps.onClick]);

  return (
    <div
      id={`widget-${id}`}
      className={print ? 'widget-print' : ''}
      onClick={baseProps.onClick}
      onMouseEnter={() => setHover(isLibrary ? false : true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        flex: 1,
        height: '100%',
        cursor: isLibrary ? 'pointer' : 'inherit',
        ...(baseProps.style || {}),
        position: 'relative',
      }}
    >
      {loading && (
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 10, marginRight: 10 }}>
          <div className="widget-loader"></div>
        </div>
      )}

      <Component {...props} />
      {isLibrary && <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, backgroundColor: '#00000000' }} />}
      {isHover && isEditing && (
        <TableActions
          style={{ position: 'absolute', top: 0, right: 5 }}
          onCopy={baseProps.onCopy}
          onDelete={baseProps.onDelete}
          onEdit={baseProps.onEdit}
        >
          <Fragment>
            <a className="d-flex mr-2" id={`widget-${widgetId}`} onClick={baseProps.onView}>
              <i className="mdi mdi-eye font-size-16 text-dark" style={{ marginTop: -4 }}></i>
            </a>
            {!!baseProps.onAdd && (
              <a className="d-flex mr-2" id={`widget-add-${widgetId}`} onClick={baseProps.onAdd}>
                <i className="font-size-16 bx bxs-plus-circle text-primary"></i>
              </a>
            )}

            {!baseProps.onView && (
              <UncontrolledTooltip placement="top" target={`widget-${widgetId}`}>
                {baseProps.children}
              </UncontrolledTooltip>
            )}
          </Fragment>
        </TableActions>
      )}

      {(isHover || !canHover) && !isEditing && !hideView && (
        <TableActions style={{ position: 'absolute', top: 0, right: 5 }}>
          <Fragment>
            <a className="d-flex mr-2" id={`widget-${widgetId}`} onClick={baseProps.onView}>
              <i className="mdi mdi-eye font-size-16 text-dark" style={{ marginTop: -4 }}></i>
            </a>
            {!!baseProps.onAdd && (
              <a className="d-flex mr-2" id={`widget-add-${widgetId}`} onClick={baseProps.onAdd}>
                <i className="font-size-16 bx bxs-plus-circle text-primary"></i>
              </a>
            )}
          </Fragment>
        </TableActions>
      )}
    </div>
  );
}
